import { Banknote, ShoppingCart, Trash2, Truck } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, EmptyState, IconButton, InlineAlert, Money } from '@/shared/ui';

import { useRememberedProductImages } from '../lib/productImage';
import type { OfflineFulfillmentType, OfflineSaleSession } from '../types';

import { OfflineProductThumb } from './OfflineProductThumb';
import { OfflineQuantityControl } from './OfflineProductCard';

export interface OfflineCartPanelProps {
  session: OfflineSaleSession;
  disabled?: boolean;
  mismatch?: boolean;
  checkoutError?: string | null;
  checkingOut?: boolean;
  onQuantity: (productId: string, quantity: number) => void;
  onRemove: (productId: string) => void;
  onReload: () => void;
  onCheckout: () => void;
  fulfillmentType?: OfflineFulfillmentType;
  lead?: ReactNode;
  children?: ReactNode;
}

export function OfflineCartPanel({
  session,
  disabled = false,
  mismatch = false,
  checkoutError,
  checkingOut = false,
  onQuantity,
  onRemove,
  onReload,
  onCheckout,
  fulfillmentType = 'IN_STORE',
  lead,
  children,
}: OfflineCartPanelProps) {
  const { t } = useTranslation('offlineOrders');
  const { cart } = session;
  const catalogImages = useRememberedProductImages();
  const homeDelivery = fulfillmentType === 'HOME_DELIVERY';

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-fg">{t('cart.title')}</h2>
          <p className="text-sm text-fg-muted">
            {t('cart.itemCount', { count: cart.items.length })}
          </p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onReload}>
          {t('cart.reload')}
        </Button>
      </div>

      {lead ? <div className="border-b border-border pb-4">{lead}</div> : null}

      {mismatch ? <InlineAlert>{t('cart.mismatch')}</InlineAlert> : null}

      {cart.items.length === 0 ? (
        <EmptyState
          className="py-8"
          icon={ShoppingCart}
          title={t('cart.emptyTitle')}
          description={t('cart.emptyDescription')}
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {cart.items.map((item) => {
            const promo = item.unitPricePaise < item.baseUnitPricePaise;
            const catalogImage = catalogImages.get(item.productId) ?? '';
            return (
              <li
                key={item.productId}
                className="flex gap-3 border-b border-border pb-3 last:border-b-0"
              >
                <OfflineProductThumb
                  src={item.productImageUrl || catalogImage}
                  fallbackSrc={catalogImage}
                  alt={item.productName}
                />
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-2 break-words text-sm font-semibold leading-5 text-fg">
                        {item.productName}
                      </p>
                      {item.unit ? (
                        <p className="truncate text-xs text-fg-muted">{item.unit}</p>
                      ) : null}
                    </div>
                    <IconButton
                      icon={Trash2}
                      label={t('cart.remove', { name: item.productName })}
                      disabled={disabled}
                      onClick={() => onRemove(item.productId)}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 text-sm">
                      <span className={promo ? 'font-medium text-success-strong' : 'text-fg'}>
                        <Money amount={item.unitPricePaise} />
                      </span>
                      {promo ? (
                        <span className="ml-2 text-xs text-fg-muted line-through">
                          <Money amount={item.baseUnitPricePaise} />
                        </span>
                      ) : null}
                    </div>
                    <OfflineQuantityControl
                      quantity={item.quantity}
                      disabled={disabled}
                      onCommit={(quantity) => onQuantity(item.productId, quantity)}
                    />
                  </div>
                  {item.mrpPaise > 0 ? (
                    <p className="text-xs text-fg-muted">
                      {t('search.mrp')} <Money amount={item.mrpPaise} />
                    </p>
                  ) : null}
                  {item.lineDiscountPaise > 0 ? (
                    <p className="text-xs text-success-strong">
                      {t('cart.lineDiscount')} <Money amount={item.lineDiscountPaise} />
                    </p>
                  ) : null}
                  {item.hasPromotion ? (
                    <p className="text-xs text-success-strong">
                      {item.promotionName || t('cart.promotion')}
                    </p>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {children}

      <dl className="flex flex-col gap-1.5 border-t border-border pt-3 text-sm">
        <Row label={t('cart.subtotal')} amount={cart.subtotalPaise} />
        <Row label={t('cart.handling')} amount={cart.handlingChargePaise} />
        {homeDelivery && cart.deliveryChargePaise === 0 ? (
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-fg-muted">{t('cart.delivery')}</dt>
            <dd className="text-xs font-medium text-fg-muted">{t('cart.deliveryPending')}</dd>
          </div>
        ) : (
          <Row label={t('cart.delivery')} amount={cart.deliveryChargePaise} />
        )}
        {cart.discountPaise > 0 ? (
          <Row label={t('cart.discount')} amount={cart.discountPaise} savings />
        ) : null}
        {session.couponDiscountPaise > 0 ? (
          <Row label={t('cart.coupon')} amount={session.couponDiscountPaise} savings />
        ) : null}
        <div className="mt-1 flex items-baseline justify-between gap-3 text-base font-semibold text-fg">
          <dt>{t('cart.payable')}</dt>
          <dd className="tabular-nums">
            <Money amount={session.totalAmountPaise} />
          </dd>
        </div>
      </dl>
      <p className="rounded-md bg-surface-muted px-3 py-2 text-xs leading-4 text-fg-muted">
        {homeDelivery ? t('cart.deliveryAtCheckout') : t('cart.noDelivery')}
      </p>

      {checkoutError ? <InlineAlert>{checkoutError}</InlineAlert> : null}

      <Button
        type="button"
        size="lg"
        className="w-full"
        disabled={disabled || mismatch || cart.items.length === 0 || checkingOut}
        onClick={onCheckout}
      >
        {homeDelivery ? (
          <Truck aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
        ) : (
          <Banknote aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
        )}
        {checkingOut
          ? homeDelivery
            ? t('checkout.homeDelivery.working')
            : t('checkout.working')
          : homeDelivery
            ? t('checkout.homeDelivery.cta')
            : t('checkout.cta')}
      </Button>
    </section>
  );
}

function Row({
  label,
  amount,
  savings = false,
}: {
  label: string;
  amount: number;
  savings?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-fg-muted">{label}</dt>
      <dd className={savings ? 'tabular-nums text-success-strong' : 'tabular-nums text-fg'}>
        <Money amount={amount} />
      </dd>
    </div>
  );
}
